import React from 'react';
import { Route, Redirect } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Switch, withRouter } from 'react-router-dom';
import { withApollo } from '@apollo/client/react/hoc';
import { flowRight as compose, get } from 'lodash';
import { withCookies } from 'react-cookie';

import apolloClient from '../services/apolloClient';
import bootstrap from '../services/bootstrap';
import routes from '../services/routes';
import GenericLoader from '@joybird/joystagram-components/dist/esm/GenericLoader';
import { GET_ACCOUNT_INFO } from '@joybird/joystagram-components/dist/esm/queries';
import { markLuxPageNavigationStart } from '@joybird/joystagram-components/dist/esm/lux';
import setHttpContextValue from '@joybird/joystagram-components/dist/esm/setHttpContextValue';
import withSetAuthTokenAndUser from '@joybird/joystagram-components/dist/esm/withSetAuthTokenAndUser';

import { isClient, isIE } from './globals';
import IEMsg from '../components/IEMsg';
import PurgeUserCache from '../components/PurgeUserCache';
import ExposeHistory from '../components/ExposeHistory';
import getRouteInfo from './helpers/getRouteInfo';
import logPageViewEvent from './helpers/logPageViewEvent';
import getPageType from './helpers/getPageType';
import { setCJAffiliateCookie } from '@joybird/joystagram-components/dist/esm/localStorage';
import {
  setLuxPageLabel,
  LUX_PAGE_LABELS,
} from '@joybird/joystagram-components/dist/esm/lux';
import SkipToContentLink from '@joybird/joystagram-components/dist/esm/SkipToContentLink';
import shouldDisablePopups from './helpers/shouldDisablePopups';
import ChatWidgets from '../components/ChatWidgets';
import getEnvValue from '@joybird/joystagram-components/dist/esm/getEnvValue';

const CONTEXT_KEY = 'useIsomorphicEffectState';

const clientVersion = getEnvValue('REACT_APP_PACKAGE_VERSION');

// List of scripts that should be affected
// by ?disablePopups URL param
const toggleablePopupScripts = [
  'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js?did=',
  'www.lightboxcdn.com/vendor/',
];

class App extends React.Component {
  constructor(props) {
    super(props);

    const { staticContext, client, setAuthTokenAndUser } = props;
    this.observer = null;

    const { cookies } = props;
    let bootstrapped = false;
    setHttpContextValue(CONTEXT_KEY, null);

    if (props.initData) {
      let finalInitData = { initData: props.initData };

      bootstrap.init(
        finalInitData,
        props.client,
        props.cookies,
        setAuthTokenAndUser
      );

      bootstrapped = true;
    }

    this.state = {
      error: false,
      bootstrapped,
      isTradeUser: false,
      isLuxInitSet: false,
    };

    //Only run bootstrap from constructor when rendering on server
    if (staticContext && Object.keys(staticContext).length) {
      bootstrap.init(staticContext, client, cookies, setAuthTokenAndUser);
    }

    const contxt = this;

    bootstrap.once('initialized', function(val) {
      if (!contxt.state.bootstrapped) {
        contxt.setState({
          bootstrapped: true,
        });
      }
    });

    this.previousLink = '';
  }

  componentDidMount() {
    const { initData = null, setAuthTokenAndUser } = this.props;

    this.unlistenHistory = this.props.history.listen(
      async (location, action) => {
        await this.sendTrackingData(undefined, location, this.previousPath);
        this.previousLink = `${window.location.origin}${this.props.location.pathname}`;
        this.previousPath = location.pathname;
      }
    );

    //Initial page view
    if (!initData) {
      bootstrap
        .init(
          { initData },
          this.props.client,
          this.props.cookies,
          setAuthTokenAndUser
        )
        .then(() => {
          this.setState({ bootstrapped: true });
          this.sendTrackingData(true);
        });
    }

    if (this.state?.bootstrapped) {
      this.sendTrackingData();
    }

    // Called before any GTM scripts are loaded in
    this.addPopupObserver();

    if (window.aa) {
      window.aa('init', {
        appId: getEnvValue('REACT_APP_ALGOLIA_APPID'),
        apiKey: getEnvValue('REACT_APP_ALGOLIA_APIKEY'),
      });
      window.algoliaInitialized = true;
    }
  }

  componentWillUnmount() {
    this.removePopupObserver();
    this.unlistenHistory();
  }

  // Mutation observer created to be able to disable
  // specified popup scripts via ?disablePopups in URL
  addPopupObserver() {
    if (!shouldDisablePopups()) return;

    this.observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (
            node.tagName === 'SCRIPT' &&
            toggleablePopupScripts.some(src => node.src.includes(src))
          ) {
            if (node.parentNode) {
              node.parentNode.removeChild(node);
            }
          }
        });
      });
    });

    this.observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  // Mutation observer clean up function
  removePopupObserver() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  async sendTrackingData(
    isInitialPageView = false,
    location = {},
    previousPath = ''
  ) {
    //Only work in the browser
    if (!isClient()) return;

    if (this.previousLink) {
      markLuxPageNavigationStart();
    }

    // get user info
    const accountResponse = await apolloClient.query({
      query: GET_ACCOUNT_INFO,
      options: {
        fetchPolicy: 'cache-and-network',
      },
    });

    const accountInfo = get(accountResponse, 'data.getAccountInfo') || {};
    const isTradeUser = !!accountInfo.is_designer;

    this.setState({ isTradeUser });

    const routeInfo = getRouteInfo();
    const pageType = getPageType(routeInfo);

    if (pageType === 'product') {
      const interval = setInterval(() => {
        if (
          window.document.location.href
            .toLowerCase()
            .includes(window.document.title.split(' ')[0].toLowerCase())
        ) {
          if (window.location.hash !== '#modal') {
            if (isInitialPageView) {
              logPageViewEvent(pageType);
            } else if (
              !isInitialPageView &&
              previousPath !== location?.pathname
            ) {
              logPageViewEvent(pageType);
            }
          }
          clearInterval(interval);
          return;
        }
      }, 30);
    } else if (pageType === 'home') {
      setLuxPageLabel(LUX_PAGE_LABELS.HOME);
      logPageViewEvent(pageType);
    } else if (
      pageType === 'category' ||
      pageType === 'swatches' ||
      pageType === 'Shop by Color'
    ) {
      setTimeout(() => {
        if (isInitialPageView) {
          logPageViewEvent(pageType);
        } else if (!isInitialPageView && previousPath !== location?.pathname) {
          logPageViewEvent(pageType);
        }
      }, 0);
    } else if (pageType === 'customerPhotos') {
      setTimeout(() => {
        if (isInitialPageView) {
          logPageViewEvent(pageType);
        } else if (!isInitialPageView && previousPath !== location?.pathname) {
          logPageViewEvent(pageType);
        }
      }, 0);
    } else {
      setTimeout(() => {
        logPageViewEvent(pageType);
      }, 0);
    }

    setCJAffiliateCookie();
    // because of this being an SPA document.referrer doesn't get updated
    // To mitigate this problem we use our own previousLink to put into GA
    setTimeout(() => {
      window.previousLink = this.previousLink;
    }, 500);
  }

  render() {
    //Only show loader on the client
    if (!this.state.bootstrapped && !this.state.error && isClient()) {
      return <GenericLoader />;
    }

    // Throw error only in browser
    if (this.state.error && isClient()) {
      throw this.state.error;
    }

    return (
      <div className="App">
        <Helmet>
          <meta property="og:site_name" content="Joybird" />
          <meta name="twitter:site" content="@wearejoybird" />
          <meta name="client-version" content={clientVersion} />
        </Helmet>
        <SkipToContentLink />

        {isIE() && <IEMsg />}

        <ChatWidgets />

        <Switch>
          <Route
            exact
            strict
            path="/search"
            render={props => {
              return (
                //Handle cases where search does not recognize query params
                <Redirect
                  to={
                    props.location.pathname.includes('?')
                      ? props.location.pathname
                      : `${props.location.pathname}/${props.location.search}${props.location.hash}`
                  }
                />
              );
            }}
          />
          {[
            ...routes.reactRedirectRoutes,
            ...(routes.reactPrintCampaignRedirectRoutes || []),
            ...routes.reactRoutes,
          ]}
        </Switch>
        <PurgeUserCache />
        <ExposeHistory />
      </div>
    );
  }
}

export default compose(
  withApollo,
  withRouter,
  withCookies,
  withSetAuthTokenAndUser
)(App);
